import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import * as HomeActions from 'src/app/store/home/home.actions';
import { Observable, Subscription, filter } from 'rxjs';
import { Scenery } from 'src/app/model/scenery';
import { selectSceneries, selectSceneryAdded, selectSteps } from 'src/app/store/scenery/scenery.selector';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { Site } from 'src/app/model/site';
import * as SceneryActions from 'src/app/store/scenery/scenery.actions';
import { SceneryStep } from 'src/app/model/scenery-step';
import { SceneryDialogComponent } from '../scenery-dialog/scenery.dialog.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { StandardDesignParameters } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-scenarios-list',
	templateUrl: './scenarios-list.component.html',
	providers: [ConfirmationService, DialogService],
	styles: `
		:host ::ng-deep .p-accordion-header-link {
			background-color: #FAFBFF !important;
			border: 1px solid #D5D5D5 !important;
			margin-bottom: 2px;
		}
		:host ::ng-deep .p-accordion-header-link:hover {
			background-color: var(--surface-500) !important;
		}
	`
})
	export class ScenariosListComponent implements OnInit, OnDestroy {
	// Observables and subscriptions
	sceneries$: Observable<Scenery[] | undefined>;
	sceneriesSub: Subscription | undefined;
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	sceneryAdded$: Observable<number | undefined>;
	sceneryAddedSub: Subscription | undefined;
	sceneryStep$: Observable<SceneryStep[] | undefined>;
	sceneryStepSub: Subscription | undefined;

	expandedPanel: number;

	sceneries: Scenery[];
	currentSite: Site;
	sceneryStep: SceneryStep[];

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	MIN_WIDTH_MX7 = 1700;

	constructor(
        private store: Store<AppState>,
        private translate: TranslateService,
        public dialogService: DialogService,
        public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	ngOnInit() {
		this.subscribeAll();
		this.onResize();
	}

	ngOnDestroy(): void {
        this.sceneriesSub?.unsubscribe();
        this.siteSub?.unsubscribe();
        this.sceneryAddedSub?.unsubscribe();
	}

	initSelectors(): void {
        // Store selectors
        this.sceneries$ = this.store.select(selectSceneries);
        this.site$ = this.store.select(selectCurrentSite);
        this.sceneryAdded$ = this.store.select(selectSceneryAdded);
        this.sceneryStep$ = this.store.select(selectSteps);
	}

	subscribeAll(): void {
		this.sceneriesSub = this.sceneries$?.subscribe(
			(sceneries) => {
				this.sceneries = sceneries;
			}
		);
		this.siteSub = this.site$?.pipe(filter(site => !!site))
			.subscribe(site => {
				this.currentSite = site;
			});
		this.sceneryAddedSub = this.sceneryAdded$?.subscribe(sceneryAdded => {
			if (sceneryAdded > 0) {
				this.expandedPanel = sceneryAdded;
				const s = new Scenery(this.currentSite.Id);
				s.Id = sceneryAdded;
				// this.addStep(s);
			}
		});
		this.sceneryStepSub = this.sceneryStep$?.subscribe(
			(sceneryStep) => {
				this.sceneryStep = sceneryStep;
			}
		);
	}

	changeTab(e) {
		this.store.dispatch(HomeActions.selectScenariosTab({ index: e.index }));
	}

	addScenery() {
		this.ref = this.dialogService.open(SceneryDialogComponent, {
			header: this.translate.instant('ADD_SCENARIO'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			// resizable: true,
			contentStyle: { overflow: 'auto' },
			data: {
				site: this.currentSite
			},
		});
 }

	editScenery(scenery: Scenery) {
		this.ref = this.dialogService.open(SceneryDialogComponent, {
			header: this.translate.instant('EDIT_SCENARIO'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			// resizable: true,
			contentStyle: { overflow: 'auto' },
			data: {
				scenery: scenery,
			},
		});
	}

	/* addStep(scenery: Scenery): void {
        this.store.dispatch(SceneryActions.resetStepDialog());
		this.ref = this.dialogService.open(StepDialogComponent, {
			header: this.translate.instant('ADD_STEP'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			// resizable: true,
			contentStyle: { overflow: 'auto' },
			data: {
				scenery: scenery,
			},
		});
	} */

	deleteScenery(scenery: Scenery): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
        this.confirmationService.confirm({
            header: this.translate.instant('DELETE_SCENARIO'),
            message: this.translate.instant('DELETE_SCENARIO_WARNING'),
            acceptLabel: this.translate.instant('CONFIRM'),
            rejectLabel: this.translate.instant('CANCEL'),
            icon: 'pi cm-alert',
            accept: () => {
                this.store.dispatch(SceneryActions.deleteScenery({ sceneryId: scenery.Id }));
            },
            reject: () => {
            }
        });
	}

	executeScenery(scenery: Scenery): void {
		this.store.dispatch(SceneryActions.executeScenery({ id: scenery.Id }));
	}

	scenerySteps(scenery: Scenery): SceneryStep[] {
		return this.sceneryStep.filter(step => step.SceneryId === scenery.Id).sort((a, b) => a.Index - b.Index);
	}


	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return (this.screenWidth <= StandardDesignParameters.MIN_MD_WIDTH);
	}
}
